import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Box,
    Button,
    Checkbox,
    Flex,
    Table, Tbody,
    Td, Text, Th, Thead, Tr,
} from "@chakra-ui/react";
import { permitteesService } from "../../../../services/permitteesService";
import { formatDate, getEmptyPageResponse } from "../../../../utils";
import { useCustomToast } from "../../../../components/CustomToast/CustomToast";
import Card from "../../../../components/Card/Card";
import CardBody from "../../../../components/Card/CardBody";
import { FilterPrintDocumentBatch } from "./FilterPrintDocumentBatch";
import { AuthContext } from "../../../../contexts/AuthContext";
import { ROUTES } from "../../../../constants/routes-path";

export const PermitteesPrintDocumentBatch = () => {
    const { t } = useTranslation();
    const [response, setResponse] = useState(undefined);
    const [searchParams, setSearchParams] = useState({
        filters: { data_inicial: formatDate(new Date()) },
    });
    const [selected, setSelected] = useState(new Set());
    const showToast = useCustomToast();
    const { getUserData } = useContext(AuthContext);
    const user = getUserData();

    const fetchData = async (params = {}) => {
        const updatedParams = { ...searchParams, ...params };
        setSearchParams(updatedParams);
        setResponse(undefined);

        try {
            const res = await permitteesService.printDocumentBatchList(updatedParams);
            setResponse(res);
        } catch (err) {
            setResponse(getEmptyPageResponse(updatedParams));
            showToast({ title: t(err), status: "error" });
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (Array.isArray(response)) {
            const allCodes = response.map((item) => item.codigo_validacao);
            setSelected(new Set(allCodes));
        } else {
            setSelected(new Set());
        }
    }, [response]);

    const toggleSelect = (code) => {
        setSelected((prev) => {
            const next = new Set(prev);
            next.has(code) ? next.delete(code) : next.add(code);
            return next;
        });
    };

    const selectAll = () => {
        if (!Array.isArray(response)) return;
        const allCodes = response.map((item) => item.codigo_validacao);
        setSelected(new Set(allCodes));
    };

    const deselectAll = () => {
        setSelected(new Set());
    };

    const handlePrint = () => {
        if (!Array.isArray(response) || selected.size === 0) {
            showToast({
                title: t("system.labels.no-items-selected"),
                status: "warning",
            });
            return;
        }
        const codes = Array.from(selected).join(',');

        window.open(
            `${window.location.origin}${ROUTES.permitteePrintDocumentBatchPdf.replace(":codes", codes).replace(":id", user?.id)}`,
            '_blank'
        );
    };

    const isLoaded = Array.isArray(response);
    const hasItems = isLoaded && response.length > 0;

    return (
        <Flex direction="column" gap="5" pt={{ base: "150px", lg: "90px" }}>
            <Flex
                w="100%"
                px="30px"
                justify="space-between"
                align="center"
                gap="4"
                direction={{ sm: "column", md: "row" }}
            >
                <FilterPrintDocumentBatch onFilter={(filters) => fetchData({ filters })} />
            </Flex>

            <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
                <CardBody>
                    {isLoaded && (
                        <Box overflowX="auto">
                            <Table>
                                <Thead>
                                    <Tr color="gray.400" fontSize={{ sm: "10px", lg: "12px" }}>
                                        <Th color="gray.400" fontSize={{ sm: "10px", lg: "12px" }}>
                                            {t("permittees.details.tab-vehicle.plate")}
                                        </Th>
                                        <Th color="gray.400" fontSize={{ sm: "10px", lg: "12px" }}>
                                            {t("permittees.details.tab-general.location")}
                                        </Th>
                                        <Th color="gray.400" fontSize={{ sm: "10px", lg: "12px" }}>
                                            {t("permittees.details.tab-general.number")}
                                        </Th>
                                        <Th color="gray.400" fontSize={{ sm: "10px", lg: "12px" }}>
                                            {t("permittees.details.tab-owner.cpf-cnpj")}
                                        </Th>
                                        <Th color="gray.400" fontSize={{ sm: "10px", lg: "12px" }}>
                                            {t("permittees.details.tab-owner.name")}
                                        </Th>
                                        <Th color="gray.400" fontSize={{ sm: "10px", lg: "12px" }}>
                                            {t("permittees.details.tab-general.payment")}
                                        </Th>
                                        <Th
                                            color="gray.400"
                                            fontSize={{ sm: "10px", lg: "12px" }}
                                            textAlign="center"
                                        >
                                            {t("system.actions.print")}
                                        </Th>
                                    </Tr>
                                </Thead>

                                <Tbody variant="simple" color="gray.500" fontSize="14px">
                                    {hasItems ? (
                                        response.map((item) => (
                                            <Tr key={item.codigo_validacao}>
                                                <Td>{item.placa_veiculo}</Td>
                                                <Td>{item.location_description}</Td>
                                                <Td>{item.permission_numero}</Td>
                                                <Td>{item.cpf_cnpj_proprietario}</Td>
                                                <Td>{item.nome_proprietario}</Td>
                                                <Td>{item.data_pagamento}</Td>
                                                <Td textAlign="center">
                                                    <Checkbox
                                                        isChecked={selected.has(item.codigo_validacao)}
                                                        onChange={() => toggleSelect(item.codigo_validacao)}
                                                        colorScheme="blue"
                                                    />
                                                </Td>
                                            </Tr>
                                        ))
                                    ) : (
                                        <Tr>
                                            <Td colSpan={7} textAlign="center" py="12">
                                                <Text fontSize="sm" color="gray.500" fontWeight="normal">
                                                    {t("system.labels.no-items-found")}
                                                </Text>
                                            </Td>
                                        </Tr>
                                    )}
                                </Tbody>
                            </Table>

                            {hasItems && (
                                <Flex justify="center" gap="4" mb="24px" mt={6}>
                                    <Button colorScheme="blue" fontSize="sm" onClick={handlePrint}>
                                        {t("system.actions.print")}
                                    </Button>
                                    <Button
                                        fontSize="sm"
                                        variant="outline"
                                        colorScheme="orange"
                                        onClick={selectAll}
                                    >
                                        {t("system.actions.mark-all")}
                                    </Button>
                                    <Button
                                        fontSize="sm"
                                        variant="outline"
                                        colorScheme="orange"
                                        onClick={deselectAll}
                                    >
                                        {t("system.actions.unmark-all")}
                                    </Button>
                                </Flex>
                            )}
                        </Box>
                    )}
                </CardBody>
            </Card>
        </Flex>
    );
};
